/**
 * Invoicing Dashboard - Main container component
 * Tabs: Billing Runs, Settlements, Invoices (spec §9, pilot-minimal)
 */

import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Tabs,
  Tab,
  Paper,
} from '@mui/material';
import {
  PlaylistPlay as RunsIcon,
  Receipt as InvoicesIcon,
  Balance as SettlementsIcon,
} from '@mui/icons-material';

import InvoiceRunList from './InvoiceRunList';
import SettlementList from './SettlementList';
import InvoiceList from './InvoiceList';

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`invoicing-tabpanel-${index}`}
      aria-labelledby={`invoicing-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

export default function InvoicingDashboard() {
  const navigate = useNavigate();
  const location = useLocation();

  // Determine active tab from URL
  const getTabIndex = () => {
    if (location.pathname.includes('/invoicing/settlements')) return 1;
    if (location.pathname.includes('/invoicing/invoices')) return 2;
    return 0; // Default to runs
  };

  const [tabValue, setTabValue] = useState(getTabIndex);
  // Cross-links: an invoice row opens its statement; a statement opens its two documents.
  const [focusSettlement, setFocusSettlement] = useState(null);
  const [focusDocuments, setFocusDocuments] = useState(null);

  const paths = ['/invoicing', '/invoicing/settlements', '/invoicing/invoices'];
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    navigate(paths[newValue], { replace: true });
  };

  const openSettlement = (statementNumber) => {
    setFocusSettlement(statementNumber);
    handleTabChange(null, 1);
  };

  const openDocuments = (statementNumber) => {
    setFocusDocuments(statementNumber);
    handleTabChange(null, 2);
  };

  return (
    <Box>
      <Paper sx={{ mb: 2 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab
            icon={<RunsIcon />}
            iconPosition="start"
            label="Billing Runs"
          />
          <Tab
            icon={<SettlementsIcon />}
            iconPosition="start"
            label="Settlements"
          />
          <Tab
            icon={<InvoicesIcon />}
            iconPosition="start"
            label="Invoices"
          />
        </Tabs>
      </Paper>

      <TabPanel value={tabValue} index={0}>
        <InvoiceRunList />
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <SettlementList
          focusStatement={focusSettlement}
          onClearFocus={() => setFocusSettlement(null)}
          onOpenDocuments={openDocuments}
        />
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <InvoiceList
          focusStatement={focusDocuments}
          onClearFocus={() => setFocusDocuments(null)}
          onOpenSettlement={openSettlement}
        />
      </TabPanel>
    </Box>
  );
}
